import { useMemo } from 'react';
import { useCategories } from './data.js';

// The category tree (Admin > Catégories) from the flat /api/categories
// list: children, paths ("Agriculture › Grains › Riz") and search.

export const norm = (s) => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
export const nodeName = (n, lang) => (n ? (lang === 'en' ? n.nameEn || n.nameFr : n.nameFr || n.nameEn) : '');

export function buildTree(list) {
  const byId = new Map(list.map((n) => [n.id, n]));
  const kids = new Map();
  const roots = [];
  const unfiled = []; // admin: items whose parent is gone, or in a parent loop ("Non classés")
  for (const n of list) {
    if (n.unfiledRoot || (n.parentId && !byId.has(n.parentId))) unfiled.push(n);
    else if (n.parentId) {
      if (!kids.has(n.parentId)) kids.set(n.parentId, []);
      kids.get(n.parentId).push(n);
    } else roots.push(n);
  }
  const children = (id) => (id ? kids.get(Number(id)) || [] : roots);
  const path = (id) => {
    const out = [];
    for (let n = byId.get(Number(id)); n && out.length < 20; n = n.parentId ? byId.get(n.parentId) : null) out.unshift(n);
    return out;
  };
  const below = (id) => { const out = []; const walk = (x) => children(x).forEach((k) => { out.push(k); walk(k.id); }); walk(id); return out; };
  const pathText = (id, lang, sep = ' › ') => path(id).map((n) => nodeName(n, lang)).join(sep);
  // Every word must appear in the item's path (either language, accents
  // ignored); items whose own name matches come first, then shorter paths.
  function search(q, lang, limit = 12) {
    const words = norm(q).split(/\s+/).filter(Boolean);
    if (!words.length) return [];
    const hits = [];
    for (const n of list) {
      const p = path(n.id);
      const hay = norm(p.map((x) => `${x.nameFr} ${x.nameEn}`).join(' '));
      if (!words.every((w) => hay.includes(w))) continue;
      const own = norm(`${n.nameFr} ${n.nameEn}`);
      const score = words.filter((w) => own.includes(w)).length * 10 - p.length + (own.startsWith(words[0]) ? 5 : 0);
      hits.push({ node: n, path: p, score });
    }
    return hits.sort((a, b) => b.score - a.score || pathText(a.node.id, lang).localeCompare(pathText(b.node.id, lang))).slice(0, limit);
  }
  return { list, byId, roots, unfiled, children, path, below, pathText, search, get: (id) => byId.get(Number(id)) || null, bySlug: (s) => list.find((n) => n.slug === s) || null };
}

export function useCategoryTree() {
  const list = useCategories();
  return useMemo(() => buildTree(list), [list]);
}

// Tile colour when an item has no photo: one per top-level sector.
const HUES = ['#46536b', '#5c7a3a', '#a5562d', '#6a4c8a', '#7a6550', '#8a6d1f', '#9a3f5c', '#3f6a7a', '#2f5c9a', '#7a5a3a', '#3a7a6a', '#6a6a4a'];
export function tileHue(tree, n) {
  const root = tree.path(n.id)[0] || n;
  const i = Math.max(0, tree.roots.indexOf(root));
  return HUES[(i >= 0 ? i : root.id) % HUES.length];
}
export const initials = (name) => String(name || '').split(/[\s&/,-]+/).filter((w) => /\p{L}|\d/u.test(w)).slice(0, 2).map((w) => w[0]).join('').toUpperCase();
